// Una scelta fra poche voci affiancate (tela, artboard 22–23): un radiogroup, con le frecce da
// tastiera. Una voce disabilitata si vede ma non si sceglie (es. «Volume SMB · in arrivo»).
import type { KeyboardEvent } from 'react';

export interface SegmentedItem<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Segmented<T extends string>({
  label,
  value,
  items,
  onChange,
}: {
  label: string;
  value: T;
  items: SegmentedItem<T>[];
  onChange: (v: T) => void;
}) {
  const enabled = items.filter((i) => !i.disabled);
  const move = (e: KeyboardEvent, step: number) => {
    e.preventDefault();
    const at = enabled.findIndex((i) => i.value === value);
    const next = enabled[(at + step + enabled.length) % enabled.length];
    if (!next) return;
    onChange(next.value);
    // il focus segue la scelta: Spazio poi conferma la voce nuova, non quella di prima
    const box = e.currentTarget as HTMLElement;
    [...box.querySelectorAll<HTMLElement>('[role="radio"]')]
      .find((b) => b.dataset.value === next.value)
      ?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') move(e, 1);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') move(e, -1);
      }}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        padding: 5,
        background: 'var(--bg-2)',
        border: 'none',
        borderRadius: 'var(--r-full)',
      }}
    >
      {items.map((i) => {
        const on = i.value === value;
        return (
          <button
            key={i.value}
            type="button"
            role="radio"
            data-value={i.value}
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            disabled={i.disabled}
            onClick={() => onChange(i.value)}
            style={{
              flex: '1 1 auto',
              minHeight: 36,
              padding: '6px 14px',
              border: 'none',
              borderRadius: 'var(--r-full)',
              font: '800 13px/18px var(--font-ui)',
              cursor: i.disabled ? 'not-allowed' : 'pointer',
              // la scelta attiva come pastiglia in rilievo (tela «PopTown Pop Style»)
              background: on ? 'var(--panel)' : 'transparent',
              color: on ? 'var(--accent)' : 'var(--dim)',
              boxShadow: on ? '0 3px 0 var(--accent)' : 'none',
              opacity: i.disabled ? 0.6 : 1,
            }}
          >
            {i.label}
          </button>
        );
      })}
    </div>
  );
}
